/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomTabsV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, Tabs, Tab } from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
function CustomTabsV2({
  tabs,
  defaultTab = 0,
  onTabChange,
  value: controlledValue,
  disableTabClick = false,
  centerTabs = false,
  renderComponentBelow = null,
  setTriggerFetchUser,
  displayZero = false,
}) {
  const [value, setValue] = useState(defaultTab);

  // Use controlled value if provided
  const currentValue = controlledValue !== undefined ? controlledValue : value;

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
    }
  }, [controlledValue]);

  const handleChange = (event, newValue) => {
    // If tab clicking is disabled, don't allow manual tab switching
    if (disableTabClick) {
      return;
    }

    setValue(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={centerTabs ? styles.tabsContainerCentered : styles.tabsContainer}
      >
        <Tabs
          value={currentValue}
          onChange={handleChange}
          textColor="inherit"
          TabIndicatorProps={{
            style: { backgroundColor: "#4caf50" },
          }}
          centered={centerTabs}
          sx={{
            ...styles.tabs,
            ...(centerTabs && {
              borderBottom: 1,
              borderColor: "divider",
            }),
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              {...a11yProps(index)}
              label={
                <span style={styles.tabLabelContainer}>
                  {tab.name}
                  {!displayZero && tab?.count !== "" && tab?.count ? (
                    <span style={styles.countBadge}>{tab.count}</span>
                  ) : null}
                  {displayZero && (
                    <span style={styles.countBadge}>{tab.count || 0}</span>
                  )}
                </span>
              }
              sx={disableTabClick ? { cursor: "default" } : {}}
            />
          ))}
        </Tabs>
      </Box>
      {renderComponentBelow && <Box>{renderComponentBelow}</Box>}
      {tabs.map((tab, index) => (
        <CustomTabPanel key={index} value={currentValue} index={index}>
          {tab.content}
        </CustomTabPanel>
      ))}
    </Box>
  );
}
export default CustomTabsV2;

function CustomTabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && (
        <Box
          sx={{
            p: 3,
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

CustomTabsV2.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number,
      content: PropTypes.node,
    }),
  ).isRequired,
  defaultTab: PropTypes.number,
  onTabChange: PropTypes.func,
  value: PropTypes.number,
  disableTabClick: PropTypes.bool,
  centerTabs: PropTypes.bool,
};

const styles = {
  tabsContainer: {
    borderBottom: 1,
    borderColor: "divider",
  },
  tabsContainerCentered: {
    display: "flex",
    justifyContent: "center",
  },
  tabs: {
    "& .MuiTab-root": {
      color: "#000000ff",
      textTransform: "none",
    },
    "& .Mui-selected": {
      color: "#000000ff !important",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#4caf50",
    },
  },
  tabLabelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  countBadge: {
    backgroundColor: "#D0D0CE",
    color: "black",
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "5px",
  },
  panelBox: {
    p: 3,
  },
};
